import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Avatar,
  Grid,
  IconButton,
  Divider,
  Paper,
  Stack,
  InputAdornment,
  Chip
} from '@mui/material';
import {
  AccountCircle,
  Edit,
  Save,
  Phone,
  Email,
  LocationOn,
  VerifiedUser,
  Badge,
  Business,
  History,
  LockReset,
  Logout
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';

const Perfil = () => {
  const { user, logout } = useAuth();
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({
    nombre: user?.nombre || 'Jon',
    apellido: user?.apellido || 'García',
    email: user?.email || 'demo@baserrri-aditu.eus',
    telefono: user?.telefono || '600123456',
    direccion: 'Camino de la Sierra, 123',
    municipio: 'Bilbao',
    provincia: 'Bizkaia',
    rega: 'ES-48-12345',
    explotacion: 'Baserri Government Demo'
  });

  const handleSave = () => {
    setIsEditing(false);
    // Logic for saving changes...
  };

  return (
    <Box sx={{ p: { xs: 2, md: 4 }, mt: 8, bgcolor: 'var(--bg-app)', minHeight: '100vh' }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" sx={{ fontWeight: 800, color: 'var(--navy-main)', letterSpacing: '-0.02em' }}>
          Configuración de Usuario
        </Typography>
        <Typography variant="body1" sx={{ color: 'var(--slate-light)', fontWeight: 500 }}>
          Gestione su identidad digital y credenciales de acceso oficial.
        </Typography>
      </Box>

      <Grid container spacing={4}>
        {/* Left Column - Profile Card */}
        <Grid item xs={12} lg={4}>
          <Card className="premium-card accent-navy">
            <CardContent sx={{ p: 4, textAlign: 'center' }}>
              <Box sx={{ position: 'relative', display: 'inline-block' }}>
                <Avatar sx={{ bgcolor: 'var(--emerald-main)', width: 120, height: 120, fontSize: 60, mx: 'auto', boxShadow: 'var(--shadow-lg)' }}>
                  {formData.nombre[0]}
                </Avatar>
                <IconButton 
                  sx={{ position: 'absolute', bottom: 0, right: 0, bgcolor: 'white', border: '1px solid var(--border-color)', '&:hover': { bgcolor: '#f0f0f0' } }}
                  size="small"
                >
                  <Edit fontSize="small" />
                </IconButton>
              </Box>
              <Typography variant="h5" sx={{ fontWeight: 800, mt: 3, color: 'var(--navy-main)' }}>
                {formData.nombre} {formData.apellido}
              </Typography>
              <Chip 
                label={user?.rol === 'ganadero' ? 'Titular de Explotación' : 'Administrador de Sistema'} 
                sx={{ mt: 1, fontWeight: 800, bgcolor: 'var(--slate-bg)', color: 'var(--navy-main)', textTransform: 'uppercase', fontSize: '10px' }} 
              />
              
              <Box sx={{ mt: 4, textAlign: 'left' }}>
                <Typography variant="caption" sx={{ fontWeight: 800, color: 'var(--slate-light)', textTransform: 'uppercase' }}>Identificador Oficial REGA</Typography>
                <Paper sx={{ p: 2, mt: 1, bgcolor: '#f8fafc', border: '1px dashed var(--border-color)', borderRadius: '12px' }}>
                  <Typography variant="body2" sx={{ fontWeight: 700, fontFamily: 'monospace' }}>{formData.rega}</Typography>
                </Paper>
              </Box>

              <Divider sx={{ my: 4 }} />
              
              <Stack spacing={2}>
                <Button fullWidth variant="outlined" startIcon={<LockReset />} sx={{ borderRadius: '12px', py: 1.5, fontWeight: 700 }}>Cambiar Contraseña</Button>
                <Button fullWidth variant="contained" color="error" startIcon={<Logout />} onClick={logout} sx={{ borderRadius: '12px', py: 1.5, fontWeight: 700 }}>Cerrar Sesión</Button>
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        {/* Right Column - Data Form */}
        <Grid item xs={12} lg={8}>
          <Card className="premium-card">
            <Box sx={{ p: 3, borderBottom: '1px solid var(--border-color)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="h6" sx={{ fontWeight: 800 }}>Información Detallada</Typography>
              {!isEditing ? (
                <Button startIcon={<Edit />} variant="outlined" size="small" onClick={() => setIsEditing(true)}>Editar Datos</Button>
              ) : (
                <Stack direction="row" spacing={1}>
                  <Button variant="outlined" size="small" color="inherit" onClick={() => setIsEditing(false)}>Cancelar</Button>
                  <Button startIcon={<Save />} variant="contained" size="small" onClick={handleSave} sx={{ bgcolor: 'var(--navy-main)' }}>Guardar</Button>
                </Stack>
              )}
            </Box>
            <CardContent sx={{ p: 4 }}>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Typography variant="caption" sx={{ fontWeight: 800, ml: 1, color: 'var(--slate-light)' }}>NOMBRE</Typography>
                  <TextField fullWidth value={formData.nombre} onChange={(e) => setFormData({...formData, nombre: e.target.value})} disabled={!isEditing} size="small" sx={{ mt: 0.5 }} />
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="caption" sx={{ fontWeight: 800, ml: 1, color: 'var(--slate-light)' }}>APELLIDOS</Typography>
                  <TextField fullWidth value={formData.apellido} onChange={(e) => setFormData({...formData, apellido: e.target.value})} disabled={!isEditing} size="small" sx={{ mt: 0.5 }} />
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="caption" sx={{ fontWeight: 800, ml: 1, color: 'var(--slate-light)' }}>CORREO ELECTRÓNICO</Typography>
                  <TextField fullWidth value={formData.email} onChange={(e) => setFormData({...formData, email: e.target.value})} disabled={!isEditing} size="small" sx={{ mt: 0.5 }} InputProps={{ startAdornment: <InputAdornment position="start"><Email fontSize="small" /></InputAdornment> }} />
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="caption" sx={{ fontWeight: 800, ml: 1, color: 'var(--slate-light)' }}>TELÉFONO DE CONTACTO</Typography>
                  <TextField fullWidth value={formData.telefono} onChange={(e) => setFormData({...formData, telefono: e.target.value})} disabled={!isEditing} size="small" sx={{ mt: 0.5 }} InputProps={{ startAdornment: <InputAdornment position="start"><Phone fontSize="small" /></InputAdornment> }} />
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="caption" sx={{ fontWeight: 800, ml: 1, color: 'var(--slate-light)' }}>DOMICILIO FISCAL / ACTIVIDAD</Typography>
                  <TextField fullWidth value={formData.direccion} onChange={(e) => setFormData({...formData, direccion: e.target.value})} disabled={!isEditing} size="small" sx={{ mt: 0.5 }} InputProps={{ startAdornment: <InputAdornment position="start"><LocationOn fontSize="small" /></InputAdornment> }} />
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="caption" sx={{ fontWeight: 800, ml: 1, color: 'var(--slate-light)' }}>MUNICIPIO</Typography>
                  <TextField fullWidth value={formData.municipio} onChange={(e) => setFormData({...formData, municipio: e.target.value})} disabled={!isEditing} size="small" sx={{ mt: 0.5 }} />
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="caption" sx={{ fontWeight: 800, ml: 1, color: 'var(--slate-light)' }}>PROVINCIA</Typography>
                  <TextField fullWidth value={formData.provincia} onChange={(e) => setFormData({...formData, provincia: e.target.value})} disabled={!isEditing} size="small" sx={{ mt: 0.5 }} />
                </Grid>
              </Grid>
              
              <Box sx={{ mt: 4, p: 3, bgcolor: '#f1f5f9', borderRadius: '16px', display: 'flex', alignItems: 'center', gap: 2 }}>
                <VerifiedUser sx={{ color: 'var(--emerald-main)' }} />
                <Box>
                  <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>Perfil Verificado</Typography>
                  <Typography variant="caption" sx={{ color: 'var(--slate-light)' }}>Sus datos han sido contrastados con la base de datos central de ganadería.</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default Perfil;
